import React, { type FormEvent, Suspense, useCallback, useMemo, useState } from 'react';
import { AudioElement, AudioElementParams, type MultiPageVideoModel } from '@bynder-studio/render-core';
import { Button, Divider, Dropdown, Flex, Form, Inspector, List, Thumbnail, token } from '@bynder/design-system';
import { useTranslate } from '@bynder/localization';
import {
    IconAdjust,
    IconArrowDown,
    IconArrowUp,
    IconCut,
    IconMore,
    IconReplace,
    IconVolumeDown,
    IconVolumeOff,
    IconVolumeUp,
} from '@bynder/icons';
import { GainSlider } from 'packages/pages/editor/RightSideMenu/FormComponents/AudioFeature/GainSlider';
import { AudioSliderInput } from 'packages/pages/editor/RightSideMenu/FormComponents/AudioFeature/AudioSliderInput';
import { FadeInput } from 'packages/pages/editor/RightSideMenu/FormComponents/AudioFeature/FadeInput';
import useEditorSelectedElement from 'packages/hooks/useEditorSelectedElement';
import useEditor from 'packages/hooks/useEditor';
import { FormSectionContent, InspectorContent, Section } from 'packages/pages/editor/RightSideMenu/index.styled';
import Duration from '../Duration';
import AssetSource from 'packages/pages/editor/RightSideMenu/Shots/AssetSource';
import PersonalUploads from 'packages/pages/editor/RightSideMenu/Shots/PersonalUploads';
import useAccessRights from 'packages/hooks/useAccessRights';
import AllowToggleVisibility from 'packages/pages/editor/RightSideMenu/Shots/AllowToggleVisibility';
import useAudio from 'packages/hooks/useAudio';
import ContentProperty from 'packages/pages/editor/RightSideMenu/Shots/ContentProperty';
import ContentPickerModal from 'packages/pages/editor/ContentPickerModal';
import { ContentType } from 'packages/pages/editor/RightSideMenu/FormComponents/BrowseButton/types';
import { CONTENT_MODAL_STEPS } from 'packages/pages/editor/ContentPickerModal/types';
import { getElementPropertiesFromSource } from 'packages/common/editor/helpers/index';
import features from 'packages/configs/features';
import { parseShortcutForTooltip } from 'packages/common/WithShortcutLabel';
import { InputWrapper, SliderWrapper } from './index.styled';
import MaybeTooltip from 'packages/common/MaybeTooltip';

const MIN_GAIN_VOLUME = -60;
const MAX_GAIN_VOLUME = 20;

const getVolumeIcon = (gain: number, isMuted: boolean) => {
    if (isMuted || gain <= MIN_GAIN_VOLUME) {
        return <IconVolumeOff style={{ flexShrink: 0, color: token.colorTextMuted }} />;
    }

    if (gain > 0) {
        return <IconVolumeUp style={{ flexShrink: 0, color: token.colorTextMuted }} />;
    }

    return <IconVolumeDown style={{ flexShrink: 0, color: token.colorTextMuted }} />;
};

const formatTime = (time: number) => {
    const minutes = Math.floor((time % 3600) / 60);
    const seconds = Math.floor(time % 60);

    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
};

const Audio = () => {
    const { translate } = useTranslate();
    const { isPlatformAllowed } = useAccessRights();
    const { selectedElement } = useEditorSelectedElement<AudioElement>();
    const { creativeModel, frameRate } = useEditor<MultiPageVideoModel>();

    const { duration, fileName } = selectedElement;

    const [showModal, setShowModal] = useState(false);
    const [modalStep, setModalStep] = useState<CONTENT_MODAL_STEPS>(CONTENT_MODAL_STEPS.CHOICE);
    const [audioSettingsExpanded, setAudioSettingsExpanded] = useState(false);

    const offsetTime = selectedElement.offsetTime ?? 0;

    const startAt = useMemo(() => formatTime(offsetTime / 1000), [offsetTime]);
    const endAt = useMemo(
        () => formatTime(offsetTime / 1000 + duration / frameRate),
        [offsetTime, duration, frameRate],
    );

    const openModal = useCallback((step: CONTENT_MODAL_STEPS) => {
        setModalStep(step);
        setShowModal(true);
    }, []);

    const onAudioChange = (params: AudioElementParams) => {
        creativeModel.updateElement(selectedElement.id, params);
    };

    const { gain, setGain, setFadeInOut, fadeIn, fadeOut, locked } = useAudio({
        audio: selectedElement,
        onAudioChange,
    });

    const handleSelectSource = (selectedSource: unknown) => {
        const params = getElementPropertiesFromSource(selectedElement, 'AUDIO', selectedSource);

        creativeModel.updateElement(selectedElement.id, params);
    };

    const defaultProps = {
        creativeModel,
        selectedElement,
        disabled: locked,
    };

    return (
        <>
            <Inspector.Header title={translate('editor.sidebar.shots.audio.tabs.text')} />
            <InspectorContent>
                <Divider />
                {isPlatformAllowed([features.CONTENT_PROPERTIES]) && (
                    <Section aria-labelledby="shots-audio-content-property">
                        <Suspense>
                            <ContentProperty
                                creativeModel={creativeModel}
                                selectedElements={[selectedElement]}
                                disabled={defaultProps.disabled}
                            />
                        </Suspense>
                    </Section>
                )}
                <Section aria-labelledby="shots-audio-settings">
                    <FormSectionContent onSubmit={preventSubmit}>
                        {showModal && (
                            <ContentPickerModal
                                type={ContentType.AUDIO}
                                show={showModal}
                                modalStep={modalStep}
                                frameRate={frameRate}
                                selectedObject={{ element: selectedElement, duration }}
                                toggleModal={setShowModal}
                                confirmSelection={(item) => {
                                    setShowModal(false);
                                    handleSelectSource(item);
                                }}
                                resizableRange
                                isEditorPage
                            />
                        )}
                        <List.Item
                            subtext={fileName ? `${startAt} - ${endAt}` : undefined}
                            rightElements={
                                <Dropdown
                                    trigger={({ isOpen: isDropdownOpen, ...triggerProps }) => (
                                        <Button
                                            isPressed={isDropdownOpen}
                                            isDisabled={defaultProps.disabled}
                                            variant="clean"
                                            icon={<IconMore />}
                                            aria-label="Actions"
                                            {...triggerProps}
                                        />
                                    )}
                                    position="bottom-right"
                                >
                                    <Dropdown.Item
                                        onClick={() => openModal(CONTENT_MODAL_STEPS.CHOICE)}
                                        icon={<IconReplace />}
                                    >
                                        {translate('editor.sidebar.shots.asset.card.label')}
                                    </Dropdown.Item>
                                    <Dropdown.Item
                                        onClick={() => openModal(CONTENT_MODAL_STEPS.TRIM)}
                                        icon={<IconCut />}
                                    >
                                        {translate('editor.sidebar.shots.asset.card.audio.action.label')}
                                    </Dropdown.Item>
                                </Dropdown>
                            }
                        >
                            {fileName || translate('editor.element.content.placeholder.audio')}
                        </List.Item>
                        <Form.Group>
                            <Flex alignItems="center" justifyContent="space-between" gap="3">
                                {getVolumeIcon(gain, selectedElement.hidden)}
                                <SliderWrapper>
                                    <GainSlider
                                        min={MIN_GAIN_VOLUME}
                                        max={MAX_GAIN_VOLUME}
                                        gain={gain}
                                        onGainChange={setGain}
                                        isDisabled={defaultProps.disabled}
                                        aria-label={translate('editor.sidebar.audio.volume.label')}
                                    />
                                </SliderWrapper>
                                <InputWrapper>
                                    <AudioSliderInput
                                        id="audio-volume-input"
                                        value={gain}
                                        min={MIN_GAIN_VOLUME}
                                        max={MAX_GAIN_VOLUME}
                                        isDisabled={defaultProps.disabled}
                                        onChange={setGain}
                                        aria-label={translate('editor.sidebar.audio.volume.label')}
                                    />
                                </InputWrapper>
                            </Flex>
                        </Form.Group>
                        <Form.Row>
                            <Form.Group>
                                <Form.Label htmlFor="audio-fade-in">
                                    {translate('editor.sidebar.audio.fade.in.label')}
                                </Form.Label>
                                <FadeInput
                                    id="audio-fade-in"
                                    value={fadeIn}
                                    isDisabled={defaultProps.disabled}
                                    onChange={setFadeInOut('fadeIn')}
                                />
                            </Form.Group>
                            <Form.Group>
                                <Form.Label htmlFor="audio-fade-out">
                                    {translate('editor.sidebar.audio.fade.out.label')}
                                </Form.Label>
                                <FadeInput
                                    id="audio-fade-out"
                                    value={fadeOut}
                                    isDisabled={defaultProps.disabled}
                                    onChange={setFadeInOut('fadeOut')}
                                />
                            </Form.Group>
                        </Form.Row>
                    </FormSectionContent>
                </Section>
                <Section aria-labelledby="shots-audio-duration">
                    <FormSectionContent>
                        <List.Item
                            thumbnail={<Thumbnail icon={<IconAdjust />} />}
                            rightElements={audioSettingsExpanded ? <IconArrowUp /> : <IconArrowDown />}
                            onClick={() => setAudioSettingsExpanded((state) => !state)}
                        >
                            {translate('design.sidebar.scene.options.expand.label', {
                                type: translate('editor.sidebar.shots.audio.tabs.text'),
                            })}
                        </List.Item>
                        {audioSettingsExpanded && <Duration {...defaultProps} frameRate={frameRate} />}
                    </FormSectionContent>
                </Section>
                <Divider />
                <Section aria-labelledby="shots-audio-asset-source">
                    <FormSectionContent>
                        <Form.Group>
                            <AssetSource disabled={defaultProps.disabled} />
                        </Form.Group>
                    </FormSectionContent>
                </Section>
                <Divider />
                <Section aria-labelledby="shots-audio-duration">
                    <FormSectionContent>
                        <Form.Group>
                            <PersonalUploads selectedElements={[selectedElement]} disabled={defaultProps.disabled} />
                        </Form.Group>
                    </FormSectionContent>
                </Section>
                <Divider />
                <Section aria-labelledby="shots-audio-visibility">
                    <FormSectionContent onSubmit={preventSubmit}>
                        <Form.Group>
                            <Form.Label>{translate('editor.element.audio_control')}</Form.Label>
                            <AllowToggleVisibility
                                creativeModel={creativeModel}
                                selectedElements={[selectedElement]}
                                disabled={defaultProps.disabled}
                            />
                        </Form.Group>
                    </FormSectionContent>
                </Section>
                <Divider />
            </InspectorContent>
        </>
    );
};

export default Audio;

function preventSubmit(e: FormEvent) {
    e.preventDefault();
}
